<template>
  <div class="terms">
    <page-header />
    <main class="page-content">
      <h1 class="page-title">
        {{ $t("termsPage.title") }}
      </h1>
      <p class="text">
        {{ $t("termsPage.subTitle") }}
      </p>
      <form @submit.prevent="nextClicked">
        <div class="form-group">
          <loading-component v-if="termsDataIsLoading" />
          <template v-else>
            <template v-if="acceptAllEnabled">
              <label class="radbox">
                <input
                  v-model="acceptAll"
                  class="radbox__input"
                  type="checkbox"
                />
                <span class="radbox__text">{{
                  $t("termsPage.acceptAll")
                }}</span>
              </label>
              <hr class="form-separator" />
            </template>
            <label
              v-for="(document, index) in registrationDocuments"
              :key="index"
              class="radbox"
            >
              <input
                v-model="v$.form[document.documentId].$model"
                class="radbox__input"
                type="checkbox"
              />
              <span class="radbox__text">
                {{ document.prefix }}
                <a :href="document.url" target="_blank" class="radbox__link">{{
                  document.documentTitle
                }}</a
                >{{ document.suffix }}
                *
                <p
                  v-if="v$.form[document.documentId].$error"
                  class="input-error"
                >
                  {{ $t("termsPage.validation.checkbox") }}
                </p>
              </span>
            </label>
          </template>
          <p v-if="dynamicConfig" class="form-error">
            {{
              $t("termsPage.notification1", {
                advanceFee: dynamicConfig.prepayAmount,
                currency: config.currency
              })
            }}
            <br />
            <template v-if="dynamicConfig.guestCommission > 0">
              {{
                $t("termsPage.notification2", {
                  handlingFee: dynamicConfig.guestCommission,
                  currency: config.currency
                })
              }}
            </template>
            <template v-else>
              {{
                $t("termsPage.notificationCardRegistrationCharge", {
                  cardRegistrationChargeFee: dynamicConfig.registrationCharge,
                  currency: config.currency
                })
              }}
            </template>
          </p>

          <input
            v-model="v$.form.email.$model"
            class="input terms__email-input"
            :class="{ error: v$.form.email.$error }"
            type="text"
            :placeholder="config.emailPlaceholder"
          />
          <p v-if="v$.form.email.$error" class="input-error">
            {{
              $t("termsPage.validation.email", { minLength: 5, maxLength: 60 })
            }}
          </p>

          <p class="text terms__email-text">
            {{ $t("termsPage.emailDescription") }}
          </p>
          <button-component
            block
            primary
            :disabled="v$.form.$invalid || termsDataIsLoading"
            :loading="submitIsLoading"
            @click="nextClicked"
          >
            {{ $t("termsPage.nextButton") }}
          </button-component>
        </div>
      </form>
    </main>
    <feedback-modal
      v-if="isFeedbackModalActive"
      :main-title="
        emailErrorMessageTitle
          ? emailErrorMessageTitle
          : $t('errors.defaultError.mainTitle')
      "
      :sub-title="
        emailErrorMessageSubtitle
          ? emailErrorMessageSubtitle
          : $t('errors.defaultError.subTitle')
      "
      :success="false"
      @confirm="handleFeedbackModalOkClick"
    />
  </div>
</template>

<script lang="ts" setup>
import FeedbackModal from "@/components/ui/FeedbackModal"
import { GuestChargingService } from "@/services/guest-charging-service"
import {
  email,
  maxLength,
  minLength,
  required,
  sameAs
} from "@vuelidate/validators"
import config from "../config/index"
import PageHeader from "../components/ui/PageHeader.vue"
import LoadingComponent from "../components/ui/LoadingComponent.vue"
import ButtonComponent from "../components/ui/ButtonComponent.vue"
import { useVuelidate } from "@vuelidate/core"
import { useReCaptcha } from "vue-recaptcha-v3"
import { computed, onMounted, reactive, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useDictionaryStore } from "../store/dictionary"
import { storeToRefs } from "pinia"
import { useUserStore } from "../store/user"

type FormType = {
  email: string
  [key: string]: string | boolean
}

const { locale, t } = useI18n()
const router = useRouter()
const recaptcha = useReCaptcha()
const dictionaryStore = useDictionaryStore()
const {
  getRegistrationDocuments: registrationDocuments,
  getTermsDataIsLoading: termsDataIsLoading,
  dynamicConfig
} = storeToRefs(dictionaryStore)
const userStore = useUserStore()
const {
  acceptedTerms,
  email: storedEmail,
  seenLandingPage
} = storeToRefs(userStore)
const { setAcceptedTerms, setEmail, setTempToken } = userStore

const form = reactive<FormType>({
  email: ""
})
const acceptAll = ref(false)
const recaptchaToken = ref<string | undefined>("")
const emailErrorMessageTitle = ref("")
const emailErrorMessageSubtitle = ref("")
const isFeedbackModalActive = ref(false)
const submitIsLoading = ref(false)

const registrationDocumentsIds = computed(() => {
  return registrationDocuments.value.map((document) => document.documentId)
})

const acceptAllEnabled = computed(() => {
  return registrationDocumentsIds.value.length > 1
})

const checkboxes = computed(() => {
  return Object.fromEntries(
    Object.entries(form).filter(([key]) => key !== "email")
  )
})

const rules = computed(() => {
  return {
    form: {
      email: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(60),
        email: email
      },
      ...Object.fromEntries(
        registrationDocumentsIds.value.map((id) => [
          id,
          { required, sameAs: sameAs(true) }
        ])
      )
    }
  }
})

const v$ = useVuelidate(rules, { form })

watch(acceptAll, (newValue) => {
  const checkboxValues = Object.values(checkboxes.value)
  const areAllCheckboxValuesEqual = new Set(checkboxValues).size <= 1

  // If acceptAll get's unchecked and all checkbox values are not equal, acceptAll field was set progmatically in "checkboxes" watcher and not by user
  if (!newValue && !areAllCheckboxValuesEqual) {
    return
  }

  registrationDocumentsIds.value.forEach((id) => {
    form[id] = newValue
  })
})

watch(registrationDocumentsIds, (ids) => {
  ids.forEach((id) => {
    form[id] = false
  })
})

watch(locale, (newValue) => {
  dictionaryStore.fetchRegistrationDocuments(newValue)
  acceptAll.value = false
})

watch(
  checkboxes,
  (newValue) => {
    acceptAll.value = Object.values(newValue).every((value) => value)
  },
  { deep: true }
)

dictionaryStore.fetchRegistrationDocuments(locale.value)

const setUser = () => {
  return {
    languagePreference: locale.value,
    isTermsAndConditionsAccepted: acceptedTerms.value,
    email: storedEmail.value
  }
}

const feedbackPopUp = () => {
  isFeedbackModalActive.value = true
}

const handleFeedbackModalOkClick = () => {
  isFeedbackModalActive.value = false
}

const getRecaptchaToken = async () => {
  await recaptcha?.recaptchaLoaded() // Wait for reCAPTCHA to load
  return await recaptcha?.executeRecaptcha("generateMobilitiUser") // Execute reCAPTCHA with action "generateMobilitiUser"
}

const nextClicked = async () => {
  recaptchaToken.value = await getRecaptchaToken()
  recaptcha?.instance?.value?.showBadge()
  setAcceptedTerms(true)
  setEmail(form.email)

  try {
    submitIsLoading.value = true
    const resp = await GuestChargingService.generateMobilitiUser(
      setUser(),
      recaptchaToken.value
    )

    if (!resp?.data?.temporaryCode) {
      feedbackPopUp()
      return
    }

    setTempToken(resp.data.temporaryCode)

    // If all reg fields are hidden
    if (Object.values(config.registrationFields).every((value) => !value)) {
      setTimeout(async () => {
        const accountingResp = await GuestChargingService.setAccountingInfo({})

        submitIsLoading.value = false

        window.location.href = accountingResp.data.simplePayUrl
      }, 5000)

      return
    }

    submitIsLoading.value = false

    router.push("registration")
  } catch (e: any) {
    console.error(e.response)
    console.error("onSubmit error: ", e.response.data)

    submitIsLoading.value = false

    if (e.response.data.errorMessage === "user.emailAlreadyInUse") {
      emailErrorMessageTitle.value = t(
        "errors.error400.userEmailAlreadyInUseTitle"
      )
      emailErrorMessageSubtitle.value = t(
        "errors.error400.userEmailAlreadyInUseSubtitle"
      )
    }

    feedbackPopUp()
  }
}

onMounted(() => {
  if (!seenLandingPage.value) {
    console.log("User has not been landing page! Redirecting..")
    router.push("/")
  }
})
</script>
